import { Link } from "react-router-dom";
import { ChevronDown, Plus } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { activityCreateOptions } from "../lib/activityCreate";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";

/**
 * Issue #79 (C1) — the Dashboard's Upcoming Activities "add" affordance.
 *
 * What it replaces: a single link reading "Schedule" that went to
 * `/performance` and nowhere else. Martin's report is that the one thing
 * the widget offers to add is a match, whatever you actually came to add.
 * So the affordance becomes a chooser: Match · Training · Development.
 *
 * PER-ITEM LINKS ARE UNTOUCHED. Every row in the list below still goes to
 * its own item's `link` (`ActivityRow` in Dashboard.jsx, fed by
 * `api/src/routes/activities.ts`). This changes the ADD action only —
 * ticket point 3 — which is why nothing here reads or rewrites the item
 * links, and why the API side of this ticket is a no-op.
 *
 * Radix `DropdownMenu`, same primitive and same `modal={false}` as
 * `AccountMenu` (#318), for the reason recorded there: a menu of
 * destinations needs roving focus, type-ahead, Escape, click-outside and
 * focus return, and Popover — a generic `role="dialog"` container — gives
 * none of it. Keyboard access is the primitive's, not hand-rolled.
 *
 * The options and the role filter live in `lib/activityCreate.js`, so what
 * a coach is and is not offered (A1) is asserted by a unit test rather
 * than only by a browser.
 */
export function UpcomingAddMenu() {
  const { user } = useAuth();
  const options = activityCreateOptions(user?.role);

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        {/* Sized and worded for the tightest cell it lives in: this sits in
            the widget header's right-hand slot at phone-390, beside a
            two-line title, above #83's past-due prompt. "Add" plus a
            chevron is the shortest honest label for a control that opens a
            menu — the old link said "Schedule", which named one of the
            three things it now offers.

            `h-11 sm:h-8` is this app's phone touch-target idiom (#106 —
            PerformanceTracking/CoachPortal/TrainingSessions all carry the
            same `h-11 sm:h-9` pair). The thing it replaced was a bare text
            link about 25px tall; the primary way to add an activity should
            not be the hardest thing on the card to hit. */}
        <button
          type="button"
          data-testid="upcoming-add"
          aria-label="Add an activity"
          className="flex h-11 shrink-0 items-center gap-1 rounded-md px-2 text-xs font-semibold text-[#0B2E59] transition-colors hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring sm:h-8"
        >
          <Plus size={12} />
          Add
          <ChevronDown size={12} />
        </button>
      </DropdownMenuTrigger>

      {/* Same width clamp and collision padding as the topbar menus
          (#189/#318): a fixed-width panel anchored to the right edge of a
          card is the overflow class this app keeps being broken by
          (#251/#263/#273/#276). Told it may shrink, it shrinks. */}
      <DropdownMenuContent
        align="end"
        collisionPadding={8}
        className="w-[min(15rem,calc(100vw-2rem))]"
        data-testid="upcoming-add-menu"
      >
        {options.map((o) => (
          // `asChild` + a real <Link>, for AccountMenu's reason: these are
          // DESTINATIONS. ⌘-click and "open in new tab" work, the status
          // bar shows where each goes, and a screen reader announces links.
          <DropdownMenuItem key={o.key} asChild className="cursor-pointer gap-2 py-2">
            <Link to={o.to} data-testid={`upcoming-add-${o.key}`}>
              <span className="min-w-0">
                <span className="block text-sm font-semibold text-slate-900">{o.label}</span>
                <span className="block text-xs text-slate-500">{o.hint}</span>
              </span>
            </Link>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
